import { otherSystemData, pass } from './../app/session/store/session.selectors';
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { ManagedLinkKey, managedLinkInfo, ManagedLinkInfo, ManagedLinkUrl } from './managedLinkKey';
import { Router } from '@angular/router';
import { Observable, of, pipe } from 'rxjs';
import { map, tap, mergeMap, filter, distinctUntilChanged, catchError, switchMap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { AppState } from 'src/app/reducers';
import {  } from 'src/app/base/home/store/acceso/acceso.selectors';
import { OtherSystemData } from 'src/app/session/store/session';
import { ToastService } from 'src/app/services/toast.service';
import { AccesoDateRequested, AccesoUrlRequested, CANT_CREATE_TOKEN, CANT_GET_ACCESO_DATE, CANT_GET_IP_CLIENTE, CredTokenRequested, HCETokenRequested, IpClienteRequested, MFTokenPersonaRequested, PortalAuditorTokenRequested, URL_NOT_FOUND } from 'src/app/base/home/store/aplicacion/aplicacion.actions';
import { aplicacionAccesoDate, aplicacionAccesoHCEToken, aplicacionAccesoIpCliente, aplicacionAccesoMfTokenPersona, aplicacionAccesoPortalAuditorToken, aplicacionAccesoUrl, aplicacionAccesoUsuarioCredToken } from 'src/app/base/home/store/aplicacion/aplicacion.selectors';
import { Voxintar } from 'src/app/shared/voxintar';


export interface ManagedLinkResolved {
	title: string,
	url: string,
}

export enum ManagedComponentType { LEGACY_SITE, LEGACY_PAGE, EXTERNAL };

const managedComponentTypeData = {};
managedComponentTypeData[ ManagedComponentType.LEGACY_SITE ] = environment.legacySiteUrl;
managedComponentTypeData[ ManagedComponentType.LEGACY_PAGE ] = environment.legacyPageUrl;
managedComponentTypeData[ ManagedComponentType.EXTERNAL ] = environment.externalUrl;

interface BindStrategy {
	varNames: string[],
	method: ( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ) => Observable<string>
}

declare let GibberishAES: any;


@Injectable()
export class ManagedLink {

	// Constantes con las variables como están en las URLs
	private readonly ID_PER_INST_VAR = "id_per_inst";
	private readonly ID_USU_VAR = "id_usu";
	private readonly USUARIO_NOMBRE_VAR = "usuario_nombre";
	private readonly ID_PER_FED_VAR = "id_per_fed";
	private readonly ID_PERS_INST_COD_VAR = "id_per_inst_cod";
	private readonly ID_USU_COD_VAR = "id_usu_cod";
	private readonly USUARIO_NOMBRE_COD_VAR = "usuario_nombre_cod";
	private readonly IP_CLIENTE_VAR = "ip_cliente";
	private readonly EPAD_RANDOM_VAR = "epad_random";
	private readonly SESSION_VAR = "session";
	private readonly PHP_SESSION_VAR = "php_session";
	private readonly SESSION_NIVEL_VAR = "session_nivel";
	private readonly USUARIO_ENCRIPTADO_VAR = "usuario_encriptado";
	private readonly MF_TOKEN_VAR = "mf_token";
	private readonly ID_PER_INST_DATE_COD_VAR = "id_per_inst_date_cod";
	private readonly JIRA_COD_VAR = "jira_cod";
	private readonly PAUDITOR_TOKEN_VAR = "pauditor_token";
	private readonly TABLERO_TOKEN_VAR = "tablero_token";
	private readonly HCE_TOKEN_VAR = "hce_token";
	private readonly CRED_TOKEN_VAR = "cred_token";

	private readonly EXTERNAL_URL_KEYS: string[] = Object.keys( environment.externalUrl );
	private readonly BEGIN_URL_VAR_SEP: string = "{";
	private readonly END_URL_VAR_SEP: string = "}";
	private readonly EPAD_RANDOM_LENGTH: number = 30;
	private readonly EPAD_RANDOM_MAX_ASCII: number = 256;
	private readonly SESSION_NIVEL_REGEXP: RegExp = new RegExp( "\(\\d+\)=" + this.BEGIN_URL_VAR_SEP + this.SESSION_NIVEL_VAR + this.END_URL_VAR_SEP );
	private readonly TABLERO_AES_KEY = "Cognos2014";

	private readonly VAR_NAME_TO_STORE_VAR = [];
	private readonly BIND_STRATEGY: BindStrategy[] = [
		{
			varNames:
			[
				this.ID_PER_INST_VAR, this.ID_USU_VAR, this.USUARIO_NOMBRE_VAR, this.ID_PER_FED_VAR,
				this.USUARIO_ENCRIPTADO_VAR, this.PHP_SESSION_VAR
			],
			method: this.replaceWithValue
		},
		{
			varNames: [ this.ID_PERS_INST_COD_VAR, this.ID_USU_COD_VAR, this.USUARIO_NOMBRE_COD_VAR, this.JIRA_COD_VAR ],
			method: this.replaceWithCodedValues
		},
		{
			varNames: [ this.EPAD_RANDOM_VAR ],
			method: this.replaceWithEpadRandom
		},
		{
			varNames: [ this.MF_TOKEN_VAR ],
			method: this.replaceWithMFTokenPersona
		},
		{
			varNames: [	this.SESSION_VAR ],
			method: this.replaceWithSession
		},
		{
			varNames: [ this.ID_PER_INST_DATE_COD_VAR ],
			method: this.replaceWithIdPerInstDateCod
		},
		{
			varNames: [ this.PAUDITOR_TOKEN_VAR ],
			method: this.replaceWithPortalAuditorToken
		},
		{
			varNames: [ this.TABLERO_TOKEN_VAR ],
			method: this.replaceWithTableroToken
		},
		{
			varNames: [ this.IP_CLIENTE_VAR ],
			method: this.replaceWithIpCliente
		},
		{
			varNames: [ this.HCE_TOKEN_VAR ],
			method: this.replaceWithHCEToken
		},
		{
			varNames: [ this.CRED_TOKEN_VAR ],
			method: this.replaceWithCredToken
		}
	];
	// Caché para todas las expresiones regulares que se generan a partir de una variable url
	private readonly VAR_REG_EXP: { [key: string]: RegExp } = {};
	private readonly NO_CACHE: string = "%NO_CACHE%";
	private readonly RANDOM_LENGTH: number = Math.pow( 10, 5 );


	constructor( private store: Store<AppState>, private toastService: ToastService, private voxintar: Voxintar ) {
		for( let key in managedLinkInfo ) {
			managedLinkInfo[key].isExternal = this.isExternalUrl( key );
		}

		// Init VAR_NAME_TO_STORE_VAR
		this.VAR_NAME_TO_STORE_VAR[	this.ID_PER_INST_VAR ] = "idPersonaInstitucional";
		this.VAR_NAME_TO_STORE_VAR[ this.ID_USU_VAR ] = "idUsuario";
		this.VAR_NAME_TO_STORE_VAR[ this.USUARIO_NOMBRE_VAR ] = "nombreUsuario";
		this.VAR_NAME_TO_STORE_VAR[ this.ID_PER_FED_VAR ] = "idPersonaFederada";
		this.VAR_NAME_TO_STORE_VAR[ this.ID_PERS_INST_COD_VAR ] = "idPersonaInstitucional";
		this.VAR_NAME_TO_STORE_VAR[ this.ID_USU_COD_VAR ] = "idUsuario";
		this.VAR_NAME_TO_STORE_VAR[ this.USUARIO_NOMBRE_COD_VAR ] = "nombreUsuario";
		this.VAR_NAME_TO_STORE_VAR[ this.PHP_SESSION_VAR ] = "phpSession";
		this.VAR_NAME_TO_STORE_VAR[ this.SESSION_VAR ] = "session";
		this.VAR_NAME_TO_STORE_VAR[ this.USUARIO_ENCRIPTADO_VAR ] = "usuarioEncriptado";
		this.VAR_NAME_TO_STORE_VAR[ this.JIRA_COD_VAR ] = "ce";
	}

	public resolveByRouter( router: Router, type: ManagedComponentType ): Observable<ManagedLinkResolved> {
		let managedUrl: string = decodeURI( router.url );
		return this.resolveByString( managedUrl, null, null, type );
	}

	public resolveByString( managedLinkKeyValue: string, ida: string, idp: string, type: ManagedComponentType ): Observable<ManagedLinkResolved> {
		let title: string = null;
		let params: string = null;
		let urlToBind$: Observable<string>;

		// Por ejemplo: managedLinkKeyValue = "/informacion-medica"

		// Recupero managedLinkUrl
		let managedLinkUrl: ManagedLinkUrl = null;
		if( managedLinkKeyValue != ManagedLinkKey.STORED_LINK ) {
			// Get url without params
			managedLinkKeyValue = this.cleanUrl( managedLinkKeyValue );
			let index = managedLinkKeyValue.indexOf('?');
			if( index > -1 ) {
				let url: string = managedLinkKeyValue;
				managedLinkKeyValue = url.substring( 0, index );
				params = url.substring( index + 1 );
			}

			// Get title
			let info: ManagedLinkInfo = environment.managedLinkInfo[ managedLinkKeyValue ];
			if( info )
				title = info.title;

			managedLinkUrl = managedComponentTypeData[ type ][ managedLinkKeyValue ];

			if( managedLinkUrl && managedLinkUrl.ida && managedLinkUrl.idp ) {
				ida = managedLinkUrl.ida;
				idp = managedLinkUrl.idp;
			}
		}

		if( managedLinkUrl && managedLinkUrl.url )
			urlToBind$ = of( this.getAbsolutePath( managedLinkUrl.url ) );
		else if( ida && idp ) {
			this.store.dispatch( new AccesoUrlRequested( { ida, idp } ) );
			urlToBind$ = this.store.select( aplicacionAccesoUrl )
				.pipe(
					filter( url => url != null ),
					distinctUntilChanged()
				);
		} else {
			// Error => No hay url a la cual ir ya sea por una url directa o por una que hay que recuperar del store usando ida y idp
			console.error( "ManagedLink: managedLinkKeyValue = " + managedLinkKeyValue );
			urlToBind$ = of( URL_NOT_FOUND );
		}

		return urlToBind$
			.pipe(
				mergeMap( urlToBind => {
					return this.bind( ida, idp, urlToBind )
						.pipe(
							map( resolvedUrl => {
								if( resolvedUrl == URL_NOT_FOUND )
									console.error( "ManagedLink: url not found!\nmanagedLinkKeyValue = " + managedLinkKeyValue + "\nurl to bind: " + urlToBind );
								return resolvedUrl;
							})
						)
				}),
				map( resolvedUrl => {
					if( resolvedUrl == URL_NOT_FOUND )
						resolvedUrl = null;
					else if( managedLinkKeyValue != null && params != null && params.length > 0 ) {
						// Agrego los parámetros a la url resuelta
						resolvedUrl = resolvedUrl + ( resolvedUrl.indexOf('?') == -1? '?': '&' ) + params;
					}
					return { title, url: resolvedUrl };
				})
			);
	}

	public getExternalUrl( managedLinkKey: ManagedLinkKey ): ManagedLinkUrl {
		return this.EXTERNAL_URL_KEYS.indexOf( managedLinkKey ) > -1? environment.externalUrl[ managedLinkKey ]: null;
	}



/*
 * private methods
 */

	private isExternalUrl( key: string ): boolean {
		return this.EXTERNAL_URL_KEYS.indexOf( key ) > -1;
	}

	private getAbsolutePath( legacyRelativeUrl: string ): string {
		let path = null;
		if( legacyRelativeUrl ) {
			if( legacyRelativeUrl.indexOf( this.NO_CACHE ) > -1 ) {
				// Reemplazo el string NO_CACHE por un string aleatorio basado en el día de hoy, la hora y un número al azar
				let todayDate: string = new Date().toISOString();
				let random: number = Math.floor( Math.random() * this.RANDOM_LENGTH );
				legacyRelativeUrl = legacyRelativeUrl.replace( this.NO_CACHE, todayDate + '-' + random.toString() );
			}

			let i = legacyRelativeUrl.indexOf( "/" );
			legacyRelativeUrl = i == 0? legacyRelativeUrl.substring( 1 ): legacyRelativeUrl;
			path = legacyRelativeUrl.indexOf( 'http' ) == 0? legacyRelativeUrl: `${environment.legacyBaseUrl}/${legacyRelativeUrl}`;
		}
		return path;
	}

	private cleanUrl( url: string ): string {
		let cleanUrl = url;
		if( url ) {
			let i = url.lastIndexOf( '/' );
			cleanUrl =  i == -1? url: url.substring( i + 1 );
		}
		return cleanUrl;
	}

	private bind( ida: string, idp: string, url: string ): Observable<string> {
		let otherSystemData$: Observable<OtherSystemData> = this.store.select( otherSystemData );
		let operators = [];

		// Obtengo las variables que posee la url
		let urlVars: Set<string> = this.findVars( url );
		if( urlVars && urlVars.size > 0 )
			urlVars.forEach( urlVar => {
				let strategy: BindStrategy = this.findStrategy( urlVar );
				if( strategy != null ) {
					operators.push(
						mergeMap( otherSystemData => {
							let resolvedUrl$: Observable<string> = strategy.method.call( this, ida, idp, url, urlVar, otherSystemData );
							return resolvedUrl$.pipe( map( resolvedUrl => {
									url = resolvedUrl;
									return otherSystemData;
								})
							);
						})
					);
				}
			});

		console.log( url );
		return otherSystemData$.pipe(
			pipe.apply( this, operators ),
			tap( () => { if( url != URL_NOT_FOUND ) console.log( url ) } ),
			map( () => url )
		);
	}

	private findVars( url: string ): Set<string> {
		let urlVars: Set<string> = null;
		if( url && url.length > 0 ) {
			let startPos: number = 0;

			while( (startPos = url.indexOf( this.BEGIN_URL_VAR_SEP, startPos )) != -1 ) {
				let endPos: number = url.indexOf( this.END_URL_VAR_SEP, startPos + 1 );
				if( endPos == -1 )
					startPos = -1;
				else {
					if( urlVars == null )
						urlVars = new Set();
					++endPos;
					urlVars.add( url.substring( startPos, endPos ) );
					startPos = endPos;
				}
			}

		}
		return urlVars;
	}

	private extractVarName( urlVar: string ): string {
		return urlVar && urlVar.length > 0? urlVar.substring( this.BEGIN_URL_VAR_SEP.length, urlVar.length - this.END_URL_VAR_SEP.length ): null;
	}

	private getVarRegExp( urlVar: string ): RegExp {
		let regExp: RegExp = this.VAR_REG_EXP[ urlVar ];
		if( regExp == null )
			regExp = this.VAR_REG_EXP[ urlVar ] = new RegExp( urlVar, 'g' );
		return regExp;
	}

	private findStrategy( urlVar: string ): BindStrategy {
		let bindStrategy: BindStrategy = null;
		let strategy: BindStrategy = null;
		for( let i = 0; i < this.BIND_STRATEGY.length && bindStrategy == null; ++i ) {
			strategy = this.BIND_STRATEGY[ i ];
			// Extract varName
			let varNameToFind: string = this.extractVarName( urlVar );
			if( strategy.varNames.find( varName => varName == varNameToFind ) != null )
				bindStrategy = strategy;
		};
		return bindStrategy;
	}

	/*
	 * Replace strategies
	 *
	 * Todas las estrategias menos replaceWithMFToken() podrían ser sincrónicas, es decir retornar la url
	 * y no un Observable de la url. Dado que replaceWithMFTokenPersona() debe obtener un token de MasterFile
	 * este método se convierte en asincrónico, es que todas las estrategias por generalización retornan un
	 * Observable<string>.
	 */

	// No usa ida ni idp
	private replaceWithValue( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		let varName: string = this.extractVarName( urlVar );
		let storeVarName: string = this.VAR_NAME_TO_STORE_VAR[ varName ];
		url = storeVarName? url.replace( this.getVarRegExp( urlVar ), otherSystemData[ storeVarName ] ): url;
		return of( url );
	}

	// No usa ida ni idp
	private replaceWithCodedValues( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		let varName: string = this.extractVarName( urlVar );
		let storeVarName: string = this.VAR_NAME_TO_STORE_VAR[ varName ];
		url = storeVarName? url.replace( this.getVarRegExp( urlVar ), btoa( otherSystemData[ storeVarName ] ) ): url;
		return of( url );
	}

	// No usa ida ni idp
	private replaceWithEpadRandom( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		// Esta función está basada en OneTimePadCreate() del archivo contenido_acceso_oracle.php
		let pad: string = '';
		let asciiValue: number;
		for( let i = 0; i < this.EPAD_RANDOM_LENGTH; ++i ) {
			asciiValue = Math.floor( Math.random() * this.EPAD_RANDOM_MAX_ASCII );
			pad += String.fromCharCode( asciiValue );
		}
		url = url.replace( this.getVarRegExp( urlVar ), btoa( pad ) );
		return of( url );
	}

	private replaceWithMFTokenPersona( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		// Esta función está basada en abreRehabilitacion() del archivo contenido_acceso_oracle.php
		this.store.dispatch( new MFTokenPersonaRequested( { nombreAplicacion: ida, nombreAplicacionPerfil: idp } ) );
		return this.store.select( aplicacionAccesoMfTokenPersona )
			.pipe(
				filter( token => token != null ),
				map( token => {
					return token == CANT_CREATE_TOKEN? URL_NOT_FOUND: url.replace( this.getVarRegExp( urlVar ), token );
				})
			);
	}

	private replaceWithSession( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		// Esta función va a manejar las variables: session y session_nivel
		// Busco si está el parámetro session_nivel en la url
		// Ejemplo de url con session_nivel:
		// http://132.1.46.105/administrador_sitio/abm_noticias/administrar.php?tmyb11={session}&2={session_nivel}&...
		// ó
		// http://132.1.46.105/administrador_sitio/abm_noticias/administrar.php?2={session_nivel}&tmyb11={session}&...
		let match = this.SESSION_NIVEL_REGEXP.exec( url );
		if( match ) {
			// Busco el nombre del parámetro que es en definitiva el que tiene el nivel de acceso
			let nivel: string = match[1];
			// Borro todo lo relacionado con este parámetro
			let before: string = url.substring( 0, match.index );
			let after: string = url.substring( match.index + match[0].length );
			if( before ) {
				if( before.endsWith( '&' ) )
					before = before.substring( 0, before.length - 1 );
				if( before.endsWith( '?' ) && after && after.startsWith( '&' ) )
					after = after.substring( 1 );
			}
			url = before + after;
			// Concateno el nivel a la sesión
			let index: number = url.indexOf( urlVar );
			url = url.substring( 0, index + urlVar.length ) + "-" + nivel + url.substring( index + urlVar.length );
		}
		return this.replaceWithValue( ida, idp, url, urlVar, otherSystemData );
	}

	private replaceWithIdPerInstDateCod( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		this.store.dispatch( new AccesoDateRequested() );
		return this.store.select( aplicacionAccesoDate )
			.pipe(
				filter( date => date != null ),
				map( date => {
					return date == CANT_GET_ACCESO_DATE? URL_NOT_FOUND:
						url.replace( this.getVarRegExp( urlVar ), btoa( otherSystemData.idPersonaInstitucional + "-" + date ) );
				})
			);
	}

	private replaceWithPortalAuditorToken( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		let idPersonaFederada: number = otherSystemData.idPersonaFederada;
		this.store.dispatch( new PortalAuditorTokenRequested( { idPersonaFederada } ) );
		return this.store.select( aplicacionAccesoPortalAuditorToken )
			.pipe(
				filter( token => token != null ),
				map( token => {
					if( token == CANT_CREATE_TOKEN ) {
						this.toastService.danger( 'La aplicación le ha denegado el ingreso.' );
						url = URL_NOT_FOUND;
					} else
						url = url.replace( this.getVarRegExp( urlVar ), token )
					return url;
				})
			);
	}

	private replaceWithTableroToken( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		return this.store.select( pass )
			.pipe(
				map( pass => url.replace( this.getVarRegExp( urlVar ), GibberishAES.enc( otherSystemData.nombreUsuario + '-' + pass, this.TABLERO_AES_KEY ) ) )
			);
	}

	private replaceWithIpCliente( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		this.store.dispatch( new IpClienteRequested() );
		return this.store.select( aplicacionAccesoIpCliente )
			.pipe(
				filter( ipCliente => ipCliente != null ),
				map( ipCliente => {
					if( ipCliente == CANT_GET_IP_CLIENTE ) {
						this.toastService.danger( 'No es posible determinar el ip del cliente' );
						url = URL_NOT_FOUND;
					} else
						url = url.replace( this.getVarRegExp( urlVar ), ipCliente );
					return url;
				})
			);
	}

	private replaceWithHCEToken( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		this.store.dispatch( new HCETokenRequested() );
		return this.store.select( aplicacionAccesoHCEToken )
			.pipe(
				filter( hceToken => hceToken != null ),
				map( hceToken => {
					if( hceToken == CANT_CREATE_TOKEN ) {
						this.toastService.danger( 'La aplicación ha denegado el acceso.' );
						url = URL_NOT_FOUND;
					} else
						url = url.replace( this.getVarRegExp( urlVar ), hceToken );
					return url;
				})
			);
	}

	private replaceWithCredToken( ida: string, idp: string, url: string, urlVar: string, otherSystemData: OtherSystemData ): Observable<string> {
		return this.store.select( pass ).pipe(
			switchMap( pass => {
				if( !otherSystemData?.nombreUsuario || !pass )
					throw new Error( "No se pueden obtener los parámetros para acceder a la aplicación" );
				let ne: string = this.voxintar.inari( otherSystemData.nombreUsuario );
				let ce: string = this.voxintar.inari( pass );
				this.store.dispatch( new CredTokenRequested( { ne, ce }) );
				return this.store.select( aplicacionAccesoUsuarioCredToken );
	 		}),
			filter( usuarioCredToken => usuarioCredToken != null ),			
			map( usuarioCredToken => {
				if( usuarioCredToken == CANT_CREATE_TOKEN ) {
					this.toastService.danger( 'Se ha generado un error de ingreso a la aplicación.' );
					url = URL_NOT_FOUND;
				} else
					url = url.replace( this.getVarRegExp( urlVar ), usuarioCredToken );
				return url;
			}),
			catchError( (error:Error) => {
				this.toastService.danger( error.message );
				return of( null );
	 		})
		);
	}

}